import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { ApiClientProvider } from './api/ApiClientProvider';
import { ApiError } from './api/ApiError';
import { AuthProvider } from './auth/AuthProvider';
import { RequireAuth } from './auth/RequireAuth';
import { AppShellLayout } from './routes/AppShellLayout';
import { HomePage } from './routes/HomePage';
import { LoginPage } from './routes/LoginPage';
import { NotFoundPage } from './routes/NotFoundPage';
import { PrivacyPage } from './routes/legal/PrivacyPage';
import { GarageListPage } from './routes/garage/GarageListPage';
import { VehicleDetailPage } from './routes/garage/VehicleDetailPage';
import { VehicleFormPage } from './routes/garage/VehicleFormPage';
import { DiscoverPage } from './routes/events/DiscoverPage';
import { EventDetailPage } from './routes/events/EventDetailPage';
import { SubmitEventPage } from './routes/events/SubmitEventPage';
import { ModerationQueuePage } from './routes/events/ModerationQueuePage';
import { MyRsvpsPage } from './routes/events/MyRsvpsPage';
import { RaceListPage } from './routes/racing/RaceListPage';
import { RaceEventDetailPage } from './routes/racing/RaceEventDetailPage';
import { RaceEventFormPage } from './routes/racing/RaceEventFormPage';
import { ThemeProvider } from './theme/ThemeProvider';

/**
 * Creates the query client.
 *
 * Retry and refetch defaults are tuned for the environment Greasemap actually runs in: event
 * venues with poor connectivity, where a single failed request is far more likely to be a flaky
 * signal than a real error.
 *
 * @returns A configured query client.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Never retry a 4xx: the request itself is the problem, so the answer will not change and
        // the reader waits through pointless round trips before seeing why.
        retry: (failureCount, error) =>
          failureCount < 2 && (!(error instanceof ApiError) || error.isRetryable),
        staleTime: 30_000,
        refetchOnWindowFocus: true,
      },
    },
  });
}

/**
 * The application root: providers, routing and chrome.
 *
 * @returns The application element.
 */
export function App(): React.JSX.Element {
  const queryClient = useMemo(() => createQueryClient(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ApiClientProvider>
          <ThemeProvider>
            <BrowserRouter>
              <Routes>
                {/* The signed-out landing sits outside the app chrome so it can render full-bleed;
                    an authenticated visitor is redirected from here into the shell (see HomePage). */}
                <Route path="/" element={<HomePage />} />

                {/* Public and outside the app chrome: a visitor must be able to read how their
                    data is handled before creating an account. */}
                <Route path="/privacy" element={<PrivacyPage />} />

                {/* Everything else renders inside the app chrome (header, nav, sign-in status). */}
                <Route element={<AppShellLayout />}>
                  {/* Public: the login page and event discovery/detail. */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/events" element={<DiscoverPage />} />
                  <Route path="/events/:eventId" element={<EventDetailPage />} />

                  {/* Gated: everything user-specific or mutating (see docs/DECISIONS.md). */}
                  <Route path="/garage" element={<RequireAuth><GarageListPage /></RequireAuth>} />
                  <Route path="/garage/new" element={<RequireAuth><VehicleFormPage /></RequireAuth>} />
                  <Route path="/garage/:vehicleId" element={<RequireAuth><VehicleDetailPage /></RequireAuth>} />
                  <Route path="/garage/:vehicleId/edit" element={<RequireAuth><VehicleFormPage /></RequireAuth>} />
                  <Route path="/events/new" element={<RequireAuth><SubmitEventPage /></RequireAuth>} />
                  <Route path="/events/moderation" element={<RequireAuth><ModerationQueuePage /></RequireAuth>} />
                  <Route path="/events/:eventId/edit" element={<RequireAuth><SubmitEventPage /></RequireAuth>} />
                  <Route path="/me/rsvps" element={<RequireAuth><MyRsvpsPage /></RequireAuth>} />

                  {/* Racing is gated behind sign-in (see docs/DECISIONS.md). */}
                  <Route path="/racing" element={<RequireAuth><RaceListPage /></RequireAuth>} />
                  <Route path="/racing/new" element={<RequireAuth><RaceEventFormPage /></RequireAuth>} />
                  <Route path="/racing/:raceEventId" element={<RequireAuth><RaceEventDetailPage /></RequireAuth>} />
                  <Route path="/racing/:raceEventId/edit" element={<RequireAuth><RaceEventFormPage /></RequireAuth>} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </ThemeProvider>
        </ApiClientProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
